import React from 'react';
import { Link } from 'react-router-dom';
import LegalPage from '../components/public/LegalPage';
import { SITE, legalEntity } from '../config/site';

// DRAFT for legal review. Every statement describes what the system does today; an attorney or the Information
// Officer must review it against POPIA before launch. If the system changes, change this text with it.
const Table = ({ head, rows }) => (
  <div className="legal__table-wrap">
    <table className="legal__table">
      <thead>
        <tr>{head.map((h) => <th key={h} scope="col">{h}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r[0]}>{r.map((cell, i) => (i === 0 ? <th key={i} scope="row">{cell}</th> : <td key={i}>{cell}</td>))}</tr>
        ))}
      </tbody>
    </table>
  </div>
);

const sections = [
  {
    id: 'who',
    title: 'Who is responsible for your information',
    body: (
      <>
        <p>
          <strong>{legalEntity()}</strong>
          {SITE.registrationNumber && <> (registration number {SITE.registrationNumber})</>} runs {SITE.name} and is the
          &quot;responsible party&quot; under the Protection of Personal Information Act (POPIA) for the personal
          information described here.
          {SITE.address && <> Our address is {SITE.address}.</>}
        </p>
        <p>
          Our Information Officer
          {SITE.informationOfficerName && <>, {SITE.informationOfficerName},</>} handles questions, requests and
          complaints about personal information. Contact them through the <Link to="/contact">contact page</Link> (choose
          &quot;My personal information&quot;)
          {SITE.informationOfficerEmail && <> or at <a href={`mailto:${SITE.informationOfficerEmail}`}>{SITE.informationOfficerEmail}</a></>}.
        </p>
        <p>
          For a rental application, the landlord or agent you apply to also receives and uses your information to make
          their decision. They are responsible for how they use it, and our Terms require them to use it only to assess
          your application.
        </p>
      </>
    )
  },
  {
    id: 'collect',
    title: 'What we collect',
    body: (
      <>
        <Table
          head={['Who', 'What we collect', 'Where it comes from']}
          rows={[
            ['Anyone with an account', 'Name, email address, mobile number (optional), a password (stored only as a one-way hash), the version of the Terms you accepted and when, and your two-factor setup if you use it.', 'You'],
            ['Applicants', 'Your ID number (stored encrypted), date of birth and nationality if you give them, current address, current landlord details, employer, job title, income, household size, pets and any notes. Documents you upload, such as your ID, payslips and bank statements. Your consent choices, and the time, IP address and device details when you made them.', 'You, and the checks you consent to'],
            ['Reference providers', 'Your name, email, phone, organisation, your relationship to the applicant, and your answers to the questionnaire.', 'The applicant, and you'],
            ['Landlords and agents', 'Your organisation’s name and type, registration number if given, your team members, the properties you add (address, rent, deposit, availability), and any proof of authority to let that you upload.', 'You'],
            ['Everyone', 'Technical information such as IP address, browser type and the pages and actions used, kept in security and audit logs. Messages you send through the contact form.', 'Your device, and you'],
          ]}
        />
        <p>
          We do not use advertising or analytics trackers on this website, and we do not buy personal information about you
          from anyone else.
        </p>
      </>
    )
  },
  {
    id: 'use',
    title: 'Why we use it',
    body: (
      <>
        <ul>
          <li><strong>To provide the service:</strong> run applications, collect and check information, prepare a report for
            the landlord, record their decision, and handle disputes, corrections and deletion requests.</li>
          <li><strong>With your consent:</strong> each check (for example identity, income, references) happens only if you
            consent to it, and you can withdraw consent at any time.</li>
          <li><strong>To keep the service safe:</strong> prevent fraud and misuse, secure accounts, and keep an audit log of
            who did what.</li>
          <li><strong>To meet legal duties</strong> and to deal with your questions and complaints.</li>
        </ul>
        <p>
          No decision about you is made automatically. A person at the landlord or agent makes it, and the reasons are
          recorded so you can see them.
        </p>
      </>
    )
  },
  {
    id: 'share',
    title: 'Who sees it',
    body: (
      <>
        <ul>
          <li>
            <strong>The landlord or agent you apply to.</strong> By default they see verified findings and explanations,
            not every raw document. Documents are shown only where necessary and authorised. Their team members see it only
            through their role in that organisation.
          </li>
          <li><strong>Reference providers</strong> you name, who see only what they need to answer the questionnaire.</li>
          <li>
            <strong>Verification sources</strong> for the checks you consent to. If we offer credit checks, they will be
            through an authorised source and only with your consent.
          </li>
          <li>
            <strong>Service providers that help us run the service</strong>, such as hosting and email delivery, who may
            process information only on our instructions.
          </li>
          <li><strong>Authorities</strong>, where the law requires it.</li>
        </ul>
        <p>
          We do not sell personal information, we do not publish it, and we do not keep a shared list of tenants. Your
          information is not shown to other landlords unless you choose to share it.
        </p>
      </>
    )
  },
  {
    id: 'keep',
    title: 'How long we keep it',
    body: (
      <>
        <Table
          head={['Information', 'How long']}
          rows={[
            ['An application’s personal information: details, ID number, uploaded documents, check results and reference answers', `Deleted automatically ${SITE.retentionMonths} months after the last activity on the application. An application with an open dispute is kept until the dispute is resolved.`],
            ['The landlord’s decision and its reasons, and proof that you gave or withdrew consent (without device details)', 'Kept after the personal information is deleted, so the landlord can show they decided accountably and we can show what you agreed to.'],
            ['Security and audit logs', `${SITE.auditLogYears} years.`],
            ['Disputes', `${SITE.disputeYears} years.`],
            ['Messages sent through the contact form', 'As long as we need to deal with your message.'],
            ['Your account', 'Until you ask us to delete it, unless we must keep something to meet the law or to resolve a dispute.'],
          ]}
        />
        <p>
          Emailed links expire after a short time. If you ask us to delete an application earlier, see &quot;Your
          rights&quot; below.
        </p>
      </>
    )
  },
  {
    id: 'rights',
    title: 'Your rights',
    body: (
      <>
        <p>You can:</p>
        <ul>
          <li><strong>See what we hold about you and download a copy.</strong> Log in and open <em>My data</em>.</li>
          <li><strong>See, and withdraw, your consent.</strong> <em>My data</em> lists every choice you made. Withdrawing stops
            further collection for that check.</li>
          <li><strong>Correct information.</strong> Edit your details while your application is open, or ask for a correction
            or raise a dispute about a report.</li>
          <li><strong>Ask for your information to be deleted.</strong> Use <em>My data</em> to request deletion of an
            application. We review the request, and when it is approved we remove your personal information from it,
            including your uploaded files, as described above.</li>
          <li><strong>Object to how we use it, or ask us to restrict it.</strong> Contact us.</li>
          <li><strong>Complain.</strong> Contact our Information Officer first. You may also complain to the
            Information Regulator (South Africa).</li>
        </ul>
        <p>
          If you do not have an account (for example you only replied to a reference request), use the{' '}
          <Link to="/contact">contact page</Link> and choose &quot;My personal information&quot;. We may need to check
          it is you before we act on a request.
        </p>
      </>
    )
  },
  {
    id: 'security',
    title: 'How we protect it',
    body: (
      <>
        <ul>
          <li>ID numbers are stored encrypted, and passwords only as one-way hashes.</li>
          <li>Uploaded documents are kept in private storage that is not reachable from the internet. Each request to open one
            is checked against the person&apos;s role and recorded.</li>
          <li>Landlord and agent accounts must use two-factor sign-in, and people join an organisation only by accepting an
            invitation. Access depends on role.</li>
          <li>Emailed links are personal, expire, and can be cancelled. Signing in with a link gives a short session.</li>
          <li>Important actions, such as viewing a document, recording a decision or changing a role, are written to an audit
            log.</li>
        </ul>
        <p>
          No system is perfectly secure. If we become aware of a breach that affects your personal information, we will
          tell you and the Information Regulator as the law requires.
        </p>
      </>
    )
  },
  {
    id: 'cookies',
    title: 'Cookies and similar technology',
    body: (
      <>
        <p>
          We store a sign-in token in your browser&apos;s local storage so you stay signed in, and we remember small
          preferences such as which organisation you last used. These are essential for the service to work. We do not use
          advertising or analytics cookies. If that ever changes, we will ask first.
        </p>
      </>
    )
  },
  {
    id: 'children',
    title: 'Children',
    body: (
      <>
        <p>The service is for people aged 18 or over. We do not knowingly collect information about children.</p>
      </>
    )
  },
  {
    id: 'transfers',
    title: 'Where information is processed',
    body: (
      <>
        <p>
          If personal information is processed outside South Africa, for example by a service provider, we make sure it is
          protected as POPIA requires.
        </p>
      </>
    )
  },
  {
    id: 'changes',
    title: 'Changes to this policy',
    body: (
      <>
        <p>
          We will update this policy when the way we handle personal information changes. The version and date are at the
          top of this page, and we will tell you about important changes.
        </p>
      </>
    )
  }
];

const summary = [
  'You choose which checks to consent to, one by one, and you can withdraw consent at any time.',
  'The landlord sees verified findings, not every document, and decides for themselves. Nothing is decided automatically.',
  `Your application information is deleted ${SITE.retentionMonths} months after the last activity, and sooner if you ask.`,
  'We do not sell your information, use advertising trackers, or keep a blacklist.'
];

const PrivacyPage = () => (
  <LegalPage
    title="Privacy Policy"
    description="How TenantVerify SA collects, uses, shares, keeps and protects personal information, and your rights."
    summary={summary}
    sections={sections}
  />
);

export default PrivacyPage;
